import type { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { authRoutes } from '@/auth/routes';
import { setAuthLostHandler } from '@/lib/api/auth';
import { moduleRoutes, publicModuleRoutes, setupModules } from '@/modules/registry';
import { AppShell } from '@/shell/AppShell';
import { LoadingState } from '@/ui';
import { appRoute } from './appRoute';
import { publicRoute } from './publicRoute';
import { NotFound, rootRoute } from './rootRoute';

appRoute.update({ component: AppShell });

// Three branches under the root: sign-in pages, public pages (capability links, no session,
// no shell) and the signed-in app.
export const routeTree = rootRoute.addChildren([...authRoutes, publicRoute.addChildren([...publicModuleRoutes]), appRoute.addChildren([...moduleRoutes])]);

function Pending() {
  return <LoadingState />;
}

export function createAppRouter(queryClient: QueryClient) {
  setupModules();
  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: Pending,
    defaultPendingMs: 400,
    defaultNotFoundComponent: NotFound,
    scrollRestoration: true,
  });
  setAuthLostHandler(() => {
    const here = router.state.location;
    if (here.pathname.startsWith('/login')) return;
    queryClient.clear();
    void router.navigate({ to: '/login', search: { next: here.href } });
  });
  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
